import React from 'react';

import './result.scss';

import T from 'prop-types';

import { withTranslation } from 'react-i18next';
import './localizations';

import _ from 'lodash';

import { Chart } from "react-google-charts";

const colors = {
  background: 'F5F5F5',
  axis: '#1c0d36',
  userScore: '#5B0B4B',
  vitaeScore: '#888888'
};

const labelsMovements = {
  desktop: {
    title: { x: 510, y: -35 },
    annotations: { x: -24, y: -20 },
    inline_annotations: { x: 10, y: 0 },
    owner: { x: 0, y: -20 },
  },

  mobile: {
    title: { x: 0, y: 0 },
    annotations: { x: 5, y: 0 },
    owner: { x: 0, y: -20 },
  }
};

class Result extends React.Component {
  constructor(props) {
    super(props);

    const locale = props.locale || 'en';

    const { t, i18n } = this.props;
    i18n.changeLanguage(locale);

    this.state = {
      t: t,
      isMobile: this.props.mobile,
      chartId: this.props.chart_id,
      scores: this.props.scores,
      vitaeScoreUpdatedAt: this.props.vitae_score_updated_at,
      height: this.props.height
    }
  }

  defaultChartOptions = () => {
    const { t, vitaeScoreUpdatedAt, height } = this.state;

    return ({
      width: '100%',
      height: height,
      enableInteractivity: false,

      titleTextStyle: { fontSize: 10, italic: true },
      // ⁦ LRI / ⁩ PDI keep the YYYY-MM-DD chunks in source order
      // when the locale is RTL (e.g. ar) — without these, the bidi algorithm
      // reorders the digit groups visually into DD-MM-YYYY.
      title: `${t('result-chart.updated-at-label')} ⁦${vitaeScoreUpdatedAt}⁩`,

      hAxis: {
        ticks: [0, 1, 2, 3, 4].map((tick) => ({v: tick, f: t(`result-chart.labels.${tick}`)})),
        minValue: 0.0,
        textStyle: { color: colors.axis }
      },
      vAxis: {
        textPosition: 'out',
        textStyle: { color: colors.axis },
      },
      legend: { position: 'top' },
      tooltip: { trigger: 'none' },
      backgroundColor: { fill: colors.background },
      colors: [colors.userScore, colors.vitaeScore],
      annotations: {
        stem: { length: 0 },
        alwaysOutside: true,
        highContrast: true,
      },
    })
  };

  desktopChartOptions = () => {
    const defaultOptions = this.defaultChartOptions();

    return (
      _.merge(defaultOptions, {
          chartArea: { left: 300, right: 150 },

          bar: {groupWidth: '55%'},

          hAxis: {
            textStyle: {
              fontSize: 14,
              stroke: false,
            },
          },

          vAxis: {
            textStyle: {
              fontSize: 12,
            },
          },

          annotations: {
            textStyle: {
              fontSize: 16,
              bold: true,
            }
          },

          legend: {
            textStyle: {
              fontSize: 12,
            }
          },
        }
      )
    )
  };

  mobileChartOptions = () => {
    const { scores } = this.state;

    const defaultOptions = this.defaultChartOptions();

    return (
      _.merge(defaultOptions, {
          chartArea: { left: 50, right: 60 },

          title: '',
          bar: {groupWidth: '55%'},

          hAxis: {
            textStyle: {
              fontSize: 10,
              stroke: false,
            },
          },

          vAxis: {
            textStyle: {
              fontSize: 6,
            },
          },

          annotations: {
            textStyle: {
              fontSize: 8,
              bold: true,
            }
          },

          legend: {
            textStyle: {
              fontSize: 8,
            }
          }
        }
      )
  )
  };

  chartOptions = () => {
    const { isMobile } = this.state;

    return ( isMobile === true ? this.mobileChartOptions() : this.desktopChartOptions() )
  };

  chartData = () => {
    const { t } = this.state;

    return ([
      [
        '',
        t('result-chart.user-score-label'),
        { role: "style" },
        { role: "annotation" },
        t('result-chart.vitae-score-label'),
        { role: "style" },
        { role: "annotation" },
      ],
      ...this.sectionsChartData()
    ])
  };

  sectionsChartData = () => {
    const { scores, t } = this.state;

    return scores.map((score) => {

      return [
        score.section,

        score.user,
        `stroke-color: ${colors.background}; stroke-width: 4`,
        score.user.toFixed(1),

        score.vitae,
        `stroke-color: ${colors.background}; stroke-width: 4`,
        score.vitae.toFixed(1)
      ]
    })
  };

  scoreToLabel = (score) => {
    const { t } = this.state;

    let label = '';

    if (score >= 0 && score <= 1) {
      label = t('result-chart.labels.0')
    } else if (score > 1 && score <= 2) {
      label = t('result-chart.labels.1')
    } else if (score > 2 && score <= 3) {
      label = t('result-chart.labels.2')
    } else if (score > 3 && score <= 4) {
      label = t('result-chart.labels.3')
    } else if (score > 4 && score <= 5) {
      label = t('result-chart.labels.4')
    }

    return label
  };

  render() {
    const { t, isMobile, chartId, scores, height } = this.state;

    // We add colors only for chart with 1 section
    const userSection = this.scoreToLabel(scores[0].user);
    const vitaeSection = this.scoreToLabel(scores[0].vitae);

    // When we need to move annotations
    const annotationStoppingPoint = scores.length > 1 ? 2.0 : 0.5;

    const chartEvents = [
      {
        eventName: 'ready',
        callback({ chartWrapper }) {
          const chartContainer = document.getElementById(chartId);

          const labels = chartContainer.getElementsByTagName('text');

          Array.prototype.forEach.call(labels, (label) => {

            // Chart title
            if (label.getAttribute('text-anchor') === 'start' && label.getAttribute('font-style') === 'italic') {
              const moveX = isMobile ? labelsMovements.mobile.title.x : labelsMovements.desktop.title.x;
              const moveY = isMobile ? labelsMovements.mobile.title.y : labelsMovements.desktop.title.y;

              label.setAttribute('x', parseFloat(label.getAttribute('x')) + moveX);
              label.setAttribute('y', parseFloat(label.getAttribute('y')) + moveY);
            }

            // Axis labels
            if (label.getAttribute('text-anchor') === 'middle') {
              label.setAttribute('text-anchor', 'start');

              // We add colors only for chart with 1 section
              if (scores.length === 1) {
                const section = label.textContent;
                if (userSection.includes(section)) {
                  label.setAttribute('style', 'font-weight: 600');
                  label.setAttribute('fill', colors.userScore);
                } else if (vitaeSection.includes(section)) {
                  label.setAttribute('style', 'font-weight: 600');
                  label.setAttribute('fill', colors.vitaeScore);
                }
              }
            }

            // Annotations
            if (!isNaN(parseFloat(label.textContent))) {
              const moveX = isMobile ? labelsMovements.mobile.annotations.x : labelsMovements.desktop.inline_annotations.x;
              const moveY = isMobile ? labelsMovements.mobile.annotations.y : labelsMovements.desktop.inline_annotations.y;

              label.setAttribute('x', parseFloat(label.getAttribute('x')) + moveX);
              label.setAttribute('y', parseFloat(label.getAttribute('y')) + moveY);

              label.setAttribute('style', 'font-weight: 600');
            }
          })
        }
      }
    ];

    return (
      <div id={ chartId } className='chart'>
        <Chart
          chartType="BarChart"
          width="100%"
          height={`${height + 50}px`}
          data={this.chartData()}
          options={this.chartOptions()}
          chartEvents={chartEvents}
        />
      </div>
    );
  }
}

Result.propTypes = {
  locale: T.string,
  mobile: T.bool,
  chart_id: T.string,
  scores: T.array,
  height: T.number,
  vitae_score_updated_at: T.string
};

export default withTranslation()(Result);
